<template>
  <b-card
    no-body
    class="profile-general-card"
  >
    <validation-observer ref="simpleRules">
      <b-card-body class="profile-general-body">
        <div
          v-if="isSideDataLoading"
          style="height: 380px"
          class="d-flex justify-content-center align-items-center"
        >
          <b-spinner label="Loading..." />
        </div>
        <template v-else>
          <div class="profile-general-head">
            <div
              v-if="isLoading"
              class="d-flex justify-content-center align-items-center profile-logo-skeleton"
            >
              <b-spinner label="Loading..." />
            </div>
            <div
              v-else-if="image"
              class="profile-logo-wrap"
            >
              <b-img
                :src="profileImageUrl"
                alt="Company logo"
                class="profile-logo-img"
              />
            </div>
            <div
              v-else
              class="profile-logo-wrap profile-logo-fallback"
            >
              My PODA
            </div>

            <template v-if="isEditMode">
              <div class="profile-head-actions">
                <h3 class="profile-company-title">
                  Edit Profile
                </h3>
                <div class="profile-upload-row">
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="profile-upload-btn"
                    @click="$refs.refInputEl.$el.click()"
                  >
                    Upload new photo
                  </b-button>
                  <b-button
                    v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                    variant="outline-secondary"
                    class="profile-reset-btn"
                    @click.prevent="resetImage"
                  >
                    Reset
                  </b-button>
                  <b-form-file
                    ref="refInputEl"
                    v-model="image"
                    accept=".jpg,.png,.gif,.jpeg,.webp"
                    :hidden="true"
                    plain
                    @input="uploadDoc($event, 'image')"
                  />
                </div>
                <small class="profile-upload-note">Allowed JPG, GIF or PNG. Max size of 800k</small>
              </div>
            </template>
            <h3
              v-else
              class="profile-company-title"
            >
              {{ firstname || 'My PODA' }}
            </h3>
          </div>

          <b-form class="profile-general-form">
            <b-row>
              <b-col
                md="6"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Company Name"
                >
                  <b-form-group class="mb-0">
                    <label class="profile-label">Company Name</label>
                    <b-form-input
                      v-model="firstname"
                      maxlength="50"
                      placeholder="Company Name"
                      :readonly="!isEditMode"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col
                md="6"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Contact Person Name"
                >
                  <b-form-group class="mb-0">
                    <label class="profile-label">Contact Person Name</label>
                    <b-form-input
                      v-model="lastname"
                      maxlength="50"
                      placeholder="Contact Person Name"
                      :readonly="!isEditMode"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col
                cols="12"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Address"
                >
                  <b-form-group class="mb-0">
                    <label class="profile-label">Address</label>
                    <b-form-input
                      v-model="address"
                      placeholder="Address"
                      :readonly="!isEditMode"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col
                md="6"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Zip Code"
                >
                  <b-form-group class="mb-0">
                    <label class="profile-label">Zip Code</label>
                    <b-form-input
                      v-model="pincode"
                      maxlength="10"
                      placeholder="Zip Code"
                      :readonly="!isEditMode"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col
                md="6"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Country"
                >
                  <b-form-group class="mb-0">
                    <label class="profile-label">Country</label>
                    <v-select
                      v-model="Country"
                      :options="getOptions()"
                      label="name"
                      placeholder="Select Country"
                      :disabled="!isEditMode"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col
                md="6"
                class="mb-1"
              >
                <b-form-group class="mb-0">
                  <label class="profile-label">State of Incorporation</label>
                  <b-form-input
                    v-model="stateOfIncorporation"
                    maxlength="120"
                    placeholder="e.g. Delaware"
                    :readonly="!isEditMode"
                  />
                </b-form-group>
              </b-col>

              <b-col
                md="6"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  rules="required|email"
                  name="Email"
                >
                  <b-form-group class="mb-0">
                    <label class="profile-label">Email</label>
                    <b-form-input
                      v-model="email"
                      maxlength="50"
                      placeholder="Email"
                      readonly
                      class="profile-readonly-field"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>

              <b-col
                md="6"
                class="mb-1"
              >
                <validation-provider
                  #default="{ errors }"
                  rules="required"
                  name="Contact"
                >
                  <b-form-group class="mb-0">
                    <label class="profile-label">Contact</label>
                    <b-form-input
                      v-model="phone"
                      maxlength="15"
                      placeholder="Contact"
                      readonly
                      class="profile-readonly-field"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>

            <b-row v-if="documents.length || isEditMode" class="mt-1">
              <b-col cols="12">
                <label class="profile-label">Company Documents</label>
                <div
                  v-if="documents.length"
                  class="profile-documents-list"
                >
                  <div
                    v-for="(doc, index) in documents"
                    :key="`${doc.url || 'doc'}-${index}`"
                    class="profile-document-item"
                  >
                    <a
                      :href="resolveDocumentUrl(doc.url)"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ docLabel(doc) }}
                    </a>
                  </div>
                </div>
                <p
                  v-else-if="!isEditMode"
                  class="text-muted mb-0"
                >
                  No documents uploaded.
                </p>
                <div
                  v-if="isEditMode"
                  class="profile-upload-row mt-50"
                >
                  <b-button
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="outline-primary"
                    class="profile-upload-btn"
                    @click="$refs.docInput.$el.click()"
                  >
                    Upload document
                  </b-button>
                  <b-form-file
                    ref="docInput"
                    v-model="pendingDocument"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                    :hidden="true"
                    plain
                    @input="uploadProfileDocument"
                  />
                  <small class="profile-upload-note d-block mt-50">PDF or image, max 10 MB</small>
                </div>
              </b-col>
            </b-row>

            <div class="profile-form-actions">
              <b-button
                v-if="!isEditMode"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="profile-edit-btn"
                @click="openEditProfile"
              >
                Edit
              </b-button>
              <template v-else>
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="profile-edit-btn mr-1"
                  :disabled="isSavingProfile"
                  @click="saveProfileChanges"
                >
                  {{ isSavingProfile ? 'Saving...' : 'Save Changes' }}
                </b-button>
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  variant="outline-secondary"
                  class="profile-cancel-btn"
                  @click="cancelEdit"
                >
                  Cancel
                </b-button>
              </template>
            </div>

            <div class="profile-danger-zone">
              <h4>Delete Account</h4>
              <p>
                Permanently remove your vendor account and sign out of all devices.
                This action cannot be undone.
              </p>
              <b-button
                variant="outline-danger"
                :disabled="deletingAccount"
                @click="openDeleteAccountModal"
              >
                Delete Account
              </b-button>
            </div>
          </b-form>
        </template>
      </b-card-body>
    </validation-observer>

    <b-modal
      v-model="deleteAccountModalOpen"
      centered
      title="Delete account"
      @hidden="resetDeleteAccountModal"
    >
      <p class="mb-1">
        Enter your password to confirm account deletion.
      </p>
      <b-form-input
        v-model="deleteAccountPassword"
        type="password"
        autocomplete="current-password"
        placeholder="Current password"
        @keyup.enter="confirmDeleteAccount"
      />
      <b-alert
        v-if="deleteAccountError"
        variant="danger"
        show
        class="mt-1 mb-0"
      >
        {{ deleteAccountError }}
      </b-alert>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="deleteAccountModalOpen = false"
        >
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="deletingAccount || !deleteAccountPassword.trim()"
          @click="confirmDeleteAccount"
        >
          <b-spinner v-if="deletingAccount" small class="mr-50" />
          Delete permanently
        </b-button>
      </template>
    </b-modal>

    <b-modal
      v-model="otpModalOpen"
      centered
      no-close-on-backdrop
      title="Verify contact change"
      @hidden="resetOtpModal"
    >
      <p class="mb-1">
        {{ otpModalHint }}
      </p>
      <b-form-input
        v-model="otpCode"
        maxlength="6"
        inputmode="numeric"
        placeholder="6-digit code"
        autocomplete="one-time-code"
        @keyup.enter="submitProfileOtp"
      />
      <b-alert
        v-if="otpError"
        variant="danger"
        show
        class="mt-1 mb-0"
      >
        {{ otpError }}
      </b-alert>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="otpModalOpen = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="otpVerifying || otpCode.trim().length < 6"
          @click="submitProfileOtp"
        >
          <b-spinner v-if="otpVerifying" small class="mr-50" />
          Verify
        </b-button>
      </template>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BButton, BForm, BFormGroup, BFormInput, BRow, BCol, BCard, BCardBody, BImg, BSpinner, BFormFile, BModal, BAlert,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import axios from '@/component/axios'
import jwtDefaultConfig from '@core/auth/jwt/jwtDefaultConfig'
import { vendorPanelApi } from '@/services/vendorPanelApi'
import VENDOR_COUNTRY_OPTIONS from '@/constants/countries'
import { notifyError, notifyInfo } from '@/utils/uiActions'
import { resolveMediaUrl } from '@/utils/mediaUrl'

export default {
  components: {
    BButton,
    BForm,
    BCardBody,
    BImg,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BCard,
    BSpinner,
    BFormFile,
    BModal,
    BAlert,
  },
  directives: {
    Ripple,
  },
  data() {
    let parsedUser = null
    try {
      parsedUser = JSON.parse(localStorage.getItem('userData'))
    } catch (e) {
      parsedUser = null
    }

    return {
      accessToken: localStorage.getItem('accessToken'),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      User: parsedUser,
      firstname: '',
      lastname: '',
      phone: '',
      email: '',
      image: '',
      address: '',
      isSideDataLoading: false,
      pincode: '',
      stateOfIncorporation: '',
      Country: '',
      isLoading: false,
      contryOption: [],
      config_email: '',
      config_password: '',
      config_host: '',
      config_port: '',
      config_encryption: '',
      config_from: '',
      originalEmail: '',
      originalPhone: '',
      otpModalOpen: false,
      otpChannel: 'email',
      otpCode: '',
      otpModalHint: '',
      otpError: '',
      otpVerifying: false,
      otpSending: false,
      emailOtpVerified: false,
      phoneOtpVerified: false,
      documents: [],
      pendingDocument: null,
      deleteAccountModalOpen: false,
      deleteAccountPassword: '',
      deleteAccountError: '',
      deletingAccount: false,
      isSavingProfile: false,
    }
  },
  computed: {
    isEditMode() {
      return this.$route.query.edit === '1'
    },
    profileImageUrl() {
      return resolveMediaUrl(this.image)
    },
  },
  watch: {
    isEditMode(isEditing) {
      if (isEditing) this.snapshotContactBaselines()
    },
  },
  mounted() {
    // this.getcountry();
    this.getProfile()
  },
  methods: {
    getOptions() {
      const options = VENDOR_COUNTRY_OPTIONS.map(item => ({ id: item.value, name: item.text }))
      if (this.Country && this.Country.name && !options.some(item => item.name === this.Country.name)) {
        options.unshift(this.Country)
      }
      return options
    },
    async uploadDoc(file, variable) {
      if (!file || typeof file === 'string') return

      try {
        const url = await vendorPanelApi.uploadAdMediaFile(file)
        this[variable] = url
        if (variable === 'image' && this.isEditMode) {
          await this.saveProfile({ skipCancel: true, successMessage: 'Profile photo updated.' })
        }
      } catch (error) {
        notifyError(this, error?.response?.data?.message || error?.message || 'Unable to upload image.')
      }
    },
    resolveDocumentUrl(url) {
      return resolveMediaUrl(url)
    },
    docLabel(doc) {
      const type = String(doc?.type || 'document').replace(/_/g, ' ')
      const uploadedAt = doc?.uploaded_at
        ? ` (${new Date(doc.uploaded_at).toLocaleDateString()})`
        : ''
      return `${type}${uploadedAt}`
    },
    async uploadProfileDocument(file) {
      if (!file || typeof file === 'string') return

      try {
        const res = await vendorPanelApi.uploadProfileDocument(file, 'company')
        const body = res?.data || {}
        const payload = body.data && typeof body.data === 'object' ? body.data : body
        if (Array.isArray(payload.documents)) {
          this.documents = payload.documents
        } else if (payload.document) {
          this.documents = [...this.documents, payload.document]
        }
        notifyInfo(body.message || 'Document uploaded successfully.')
      } catch (error) {
        notifyError(error?.response?.data?.message || error?.message || 'Unable to upload document.')
      } finally {
        this.pendingDocument = null
      }
    },
    openEditProfile() {
      this.snapshotContactBaselines()
      this.$router.push({
        name: this.$route.name,
        query: { ...this.$route.query, edit: '1' },
      }).catch(() => {})
    },
    snapshotContactBaselines() {
      this.originalEmail = String(this.email || '').trim()
      this.originalPhone = String(this.phone || '').trim()
      this.emailOtpVerified = false
      this.phoneOtpVerified = false
    },
    cancelEdit() {
      const query = { ...this.$route.query }
      delete query.edit
      this.getProfile()
      this.$router.push({
        name: this.$route.name,
        query,
      }).catch(() => {})
    },
    resetImage() {
      this.image = ''
    },
    async getProfile() {
      this.isSideDataLoading = true
      this.isLoading = true
      try {
        const res = await vendorPanelApi.profile()
        const body = res?.data || {}
        const data = body.data && typeof body.data === 'object' ? body.data : body
        this.firstname = data.company_name || data.compny_name || ''
        this.lastname = data.contact_person_name || ''
        this.phone = data.contact || ''
        this.email = data.email || ''
        this.address = data.address || data.adress || ''
        this.pincode = data.pincode || ''
        this.stateOfIncorporation = data.state_of_incorporation || ''
        this.Country = data.country ? { id: data.country, name: data.country } : ''
        this.image = data.image || ''
        this.documents = Array.isArray(data.documents) ? data.documents : []
        this.originalEmail = this.email
        this.originalPhone = this.phone
        this.emailOtpVerified = false
        this.phoneOtpVerified = false
      } finally {
        this.isSideDataLoading = false
        this.isLoading = false
      }
    },
    validateProfileForm() {
      if (!String(this.firstname || '').trim()) {
        notifyError(this, 'Company name is required.')
        return false
      }
      if (!String(this.lastname || '').trim()) {
        notifyError(this, 'Contact person name is required.')
        return false
      }
      if (!String(this.address || '').trim()) {
        notifyError(this, 'Address is required.')
        return false
      }
      if (!String(this.pincode || '').trim()) {
        notifyError(this, 'Zip code is required.')
        return false
      }
      if (!this.Country || !this.Country.name) {
        notifyError(this, 'Please select a country.')
        return false
      }
      if (!String(this.email || '').trim()) {
        notifyError(this, 'Email is required.')
        return false
      }
      if (!String(this.phone || '').trim()) {
        notifyError(this, 'Contact number is required.')
        return false
      }
      return true
    },
    async saveProfileChanges() {
      if (this.isSavingProfile) return
      if (!this.validateProfileForm()) return
      await this.saveProfile()
    },
    normalizePhone(value) {
      return String(value || '').replace(/\D/g, '')
    },
    hasEmailChanged() {
      return String(this.email || '').trim().toLowerCase() !== String(this.originalEmail || '').trim().toLowerCase()
    },
    hasPhoneChanged() {
      return this.normalizePhone(this.phone) !== this.normalizePhone(this.originalPhone)
    },
    needsContactVerification() {
      return (this.hasEmailChanged() && !this.emailOtpVerified)
        || (this.hasPhoneChanged() && !this.phoneOtpVerified)
    },
    resetOtpModal() {
      this.otpCode = ''
      this.otpError = ''
    },
    async startContactVerification() {
      this.otpError = ''
      if (this.hasEmailChanged() && !this.emailOtpVerified) {
        await this.sendProfileOtp('email')
        return
      }
      if (this.hasPhoneChanged() && !this.phoneOtpVerified) {
        await this.sendProfileOtp('phone')
        return
      }
      await this.saveProfile()
    },
    async sendProfileOtp(channel) {
      this.otpChannel = channel
      this.otpSending = true
      this.otpError = ''
      try {
        if (channel === 'email') {
          const email = String(this.email || '').trim().toLowerCase()
          const res = await axios.post(jwtDefaultConfig.sendEmailOtpEndpoint, { email })
          if (!res.data?.success) {
            throw new Error(res.data?.message || 'Unable to send email verification code.')
          }
          this.otpModalHint = `Enter the 6-digit code sent to ${email}.`
        } else {
          const phone = this.normalizePhone(this.phone)
          const res = await axios.post(jwtDefaultConfig.sendMobileOtpEndpoint, { phone })
          if (!res.data?.success) {
            throw new Error(res.data?.message || 'Unable to send mobile verification code.')
          }
          this.otpModalHint = `Enter the 6-digit code sent to ${this.phone}.`
        }
        this.otpCode = ''
        this.otpModalOpen = true
        notifyInfo(this, 'Verification code sent.')
      } catch (error) {
        const message = error?.response?.data?.message || error.message || 'Unable to send verification code.'
        this.otpError = message
        notifyError(this, message)
      } finally {
        this.otpSending = false
      }
    },
    async submitProfileOtp() {
      const otp = String(this.otpCode || '').trim()
      if (otp.length < 6) {
        this.otpError = 'Enter the 6-digit verification code.'
        return
      }
      this.otpVerifying = true
      this.otpError = ''
      try {
        if (this.otpChannel === 'email') {
          const email = String(this.email || '').trim().toLowerCase()
          const res = await axios.post(jwtDefaultConfig.verifyEmailOtpEndpoint, { email, otp })
          if (!res.data?.success) {
            throw new Error(res.data?.message || 'Invalid email verification code.')
          }
          this.emailOtpVerified = true
        } else {
          const phone = this.normalizePhone(this.phone)
          const res = await axios.post(jwtDefaultConfig.verifyMobileOtpEndpoint, { phone, otp })
          if (!res.data?.success) {
            throw new Error(res.data?.message || 'Invalid mobile verification code.')
          }
          this.phoneOtpVerified = true
        }
        this.otpModalOpen = false
        if (this.needsContactVerification()) {
          await this.startContactVerification()
        } else {
          await this.saveProfile()
        }
      } catch (error) {
        this.otpError = error?.response?.data?.message || error.message || 'Verification failed.'
      } finally {
        this.otpVerifying = false
      }
    },
    buildProfileEditPayload() {
      return {
        company_name: String(this.firstname || '').trim(),
        contact_person_name: String(this.lastname || '').trim(),
        address: String(this.address || '').trim(),
        country: this.Country && this.Country.name ? String(this.Country.name).trim() : '',
        pincode: String(this.pincode || '').trim(),
        state_of_incorporation: String(this.stateOfIncorporation || '').trim(),
        email: String(this.email || '').trim(),
        contact: String(this.phone || '').trim(),
        image: typeof this.image === 'string' ? this.image : '',
      }
    },
    async saveProfile(options = {}) {
      const { skipCancel = false, successMessage = '' } = options
      if (this.isSavingProfile) return

      const profilePayload = this.buildProfileEditPayload()
      this.isSavingProfile = true
      try {
        const response = await vendorPanelApi.editProfile(profilePayload)
        const successText = successMessage
          || response?.data?.message
          || response?.data?.success
          || 'Profile updated successfully'
        this.$swal({
          title: 'Submitted',
          text: successText,
          icon: 'success',
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
        const userData = {
          ...(this.User || {}),
          ...profilePayload,
          id: this.User?.id,
          name: profilePayload.contact_person_name || profilePayload.company_name,
          compny_name: profilePayload.company_name,
          image: profilePayload.image,
        }
        localStorage.setItem('userData', JSON.stringify(userData))
        this.snapshotContactBaselines()
        await this.getProfile()
        if (!skipCancel) {
          this.cancelEdit()
        }
      } catch (error) {
        const message = error?.response?.data?.message || 'Unable to update profile.'
        notifyError(this, message)
      } finally {
        this.isSavingProfile = false
      }
    },
    openDeleteAccountModal() {
      this.deleteAccountError = ''
      this.deleteAccountPassword = ''
      this.deleteAccountModalOpen = true
    },
    resetDeleteAccountModal() {
      this.deleteAccountPassword = ''
      this.deleteAccountError = ''
    },
    async confirmDeleteAccount() {
      if (this.deletingAccount || !this.deleteAccountPassword.trim()) return
      this.deletingAccount = true
      this.deleteAccountError = ''
      try {
        await vendorPanelApi.deleteAccount({ password: this.deleteAccountPassword })
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('userData')
        this.deleteAccountModalOpen = false
        await this.$router.push({ name: 'auth-login' }).catch(() => {})
        notifyInfo(this, 'Your account has been deleted.', 'Account deleted')
      } catch (error) {
        this.deleteAccountError = error?.response?.data?.message || 'Unable to delete account.'
      } finally {
        this.deletingAccount = false
      }
    },
  },
}
</script>
<style scoped lang="scss">
.profile-general-card {
  border: 1px solid #f1f1f6;
  box-shadow: 0 2px 8px rgba(39, 44, 62, 0.05);
  border-radius: 8px;
}

.profile-general-body {
  padding: 1.1rem 1rem 1rem;

  @media (min-width: 992px) {
    padding: 1.75rem 1.5rem 1.15rem;
  }
}

.profile-general-head {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.65rem;
  flex-wrap: wrap;
}

.profile-logo-skeleton,
.profile-logo-wrap {
  width: 72px;
  height: 72px;
  border-radius: 6px;
  border: 1px solid #ececf3;
  overflow: hidden;
  flex-shrink: 0;
}

.profile-logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-logo-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #072646;
  color: #fff;
  font-size: 1rem;
  letter-spacing: 0.08em;
  font-weight: 700;
}

.profile-company-title {
  margin: 0;
  color: #2f2f3c;
  font-size: 1.4rem;
  line-height: 1.1;
  font-weight: 700;
}

.profile-head-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.profile-upload-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.profile-upload-btn {
  font-size: 0.9rem;
}

.profile-reset-btn {
  font-size: 0.88rem;
}

.profile-upload-note {
  color: #9a96ad;
  font-size: 0.82rem;
}

.profile-documents-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.profile-document-item a {
  color: #7367f0;
  text-decoration: none;
}

.profile-document-item a:hover {
  text-decoration: underline;
}

.profile-label {
  font-weight: 500;
  font-size: 0.92rem;
  color: #53506a;
  margin-bottom: 0.35rem;
}

.profile-general-form {
  ::v-deep .form-control {
    border: 1px solid #ececf3;
    min-height: 2.35rem;
    color: #605d73;
    font-size: 0.9rem;
  }

  ::v-deep .vs__dropdown-toggle {
    border-color: #ececf3;
    min-height: 2.35rem;
  }

  ::v-deep .profile-readonly-field {
    background-color: #f8f8fb !important;
    cursor: not-allowed;
  }
}

.profile-form-actions {
  margin-top: 0.5rem;
}

.profile-edit-btn {
  min-width: 84px;
}

.profile-cancel-btn {
  min-width: 84px;
}
</style>
